import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import { Box, Typography } from "@mui/material";
import { io } from "socket.io-client";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const MAX_DATA_POINTS = 20; // Show last 20 data points

const SpeedGraph = ({ userId, nasIpAddress }) => {
  const [socket, setSocket] = useState(null);
  const [currentSpeeds, setCurrentSpeeds] = useState({
    download: 0,
    upload: 0,
  });

  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [
      {
        label: "Download Speed (Mbps)",
        data: [],
        borderColor: "rgba(75, 192, 192, 1)",
        backgroundColor: "rgba(75, 192, 192, 0.2)",
        tension: 0.4,
        fill: true,
      },
      {
        label: "Upload Speed (Mbps)",
        data: [],
        borderColor: "rgba(153, 102, 255, 1)",
        backgroundColor: "rgba(153, 102, 255, 0.2)",
        tension: 0.4,
        fill: true,
      },
    ],
  });

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: true,
        text: `Network Speed - Current: ${currentSpeeds.download.toFixed(2)} Mbps ↓ / ${currentSpeeds.upload.toFixed(2)} Mbps ↑`,
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw.toFixed(2)} Mbps`,
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Speed (Mbps)",
        },
      },
      x: {
        title: {
          display: true,
          text: "Time",
        },
      },
    },
    animation: {
      duration: 1000,
    },
  };

  useEffect(() => {
    const newSocket = io(process.env.REACT_APP_SOCKET_URL, {
      transports: ["websocket"],
      query: { userName: userId },
    });

    setSocket(newSocket);

    newSocket.emit("messageSendToUser", { userName: userId, nasIpAddress: nasIpAddress });

    newSocket.on("message", (data) => {
      console.log("Message received:", data);
      if (data.userName === userId) {
        // Update current speeds display
        setCurrentSpeeds({
          download: data.downloadSpeed,
          upload: data.uploadSpeed,
        });

        // Update chart data
        updateChartData(data.downloadSpeed, data.uploadSpeed);
      }
    });

    return () => newSocket.close();
  }, [userId]);

  const updateChartData = (downloadSpeed, uploadSpeed) => {
    setChartData((prev) => {
      const newLabel = new Date().toLocaleTimeString();

      // Keep only the last MAX_DATA_POINTS entries
      const newLabels = [...prev.labels, newLabel].slice(-MAX_DATA_POINTS);
      const newDownloadData = [...prev.datasets[0].data, downloadSpeed].slice(-MAX_DATA_POINTS);
      const newUploadData = [...prev.datasets[1].data, uploadSpeed].slice(-MAX_DATA_POINTS);

      return {
        labels: newLabels,
        datasets: [
          { ...prev.datasets[0], data: newDownloadData },
          { ...prev.datasets[1], data: newUploadData },
        ],
      };
    });
  };

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Network Speed Monitoring
      </Typography>
      <Box mb={2}>
        <Typography variant="h6">
          Current Speed:
          <span style={{ color: "rgba(75, 192, 192, 1)" }}>
            {" "}
            {currentSpeeds.download.toFixed(2)} Mbps ↓{" "}
          </span>
          /
          <span style={{ color: "rgba(153, 102, 255, 1)" }}>
            {" "}
            {currentSpeeds.upload.toFixed(2)} Mbps ↑
          </span>
        </Typography>
      </Box>
      <Line data={chartData} options={options} />
    </Box>
  );
};

export default SpeedGraph;
